import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
 identitySetTotpStart,
 identitySetVerificationStart,
 identityVerifyTotpStart,
} from '../../../../redux/identity/identitySlice';
import {
 usersGetSelfStart,
 usersGetUserGroupsStart,
 usersUpdateSelfStart,
} from '../../../../redux/users/usersSlice';
import { RootState } from '../../../../redux/store';
import { getCookieCustom } from '../../../../utils/cookies';
import { UserProfileFormData } from '../types';

const defaultValues: UserProfileFormData = {
 Name: '',
 Surname: '',
 Email: '',
 Phone: '',
 Customer: undefined,
 UserGroups: [],
 SendMethods: [],
 //  OldPassword: '',
 //  NewPassword: '',
 //  RepeatPassword: '',
};

export const useUserProfileForm = (open: boolean, onClose: () => void) => {
 const { i18n } = useTranslation();
 const dispatch = useAppDispatch();
 const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
 const [activeTab, setActiveTab] = useState(0);
 const [qrCode, setQrCode] = useState<string | null>(null);
 const [verificationCode, setVerificationCode] = useState<string>('');
 const [twoFactorAuthMethod, setTwoFactorAuthMethod] = useState<1 | 2 | null>(
  null,
 );

 const identityState = useAppSelector((state: RootState) => state.identity);
 const usersState = useAppSelector((state: RootState) => state.users);
 const self = usersState.user;
 const groups = usersState.groups || [];
 const loading = { ...identityState.loading, ...usersState.loading };
 const success = { ...identityState.success, ...usersState.success };

 const {
  control,
  handleSubmit,
  reset,
  watch,
  setValue,
  formState: { errors },
 } = useForm<UserProfileFormData>({ defaultValues });

 useEffect(() => {
  if (open) {
   dispatch(usersGetSelfStart({}));
   dispatch(usersGetUserGroupsStart({}));
  }
 }, [open, dispatch]);

 useEffect(() => {
  if (self && success?.usersGetSelf) {
   reset({
    Name: self.Name || '',
    Surname: self.Surname || '',
    Email: self.Email || '',
    Phone: self.Phone || '',
    Customer: self.Customer || undefined,
    UserGroups: self.UserGroups || [],
    SendMethods: self.SendMethods || [],
    // OldPassword: '',
    // NewPassword: '',
    // RepeatPassword: '',
   });
   setTwoFactorEnabled(self.TwoFAEnabled || false);
  }
 }, [self, success?.usersGetSelf, reset]);

 useEffect(() => {
  if (success?.usersUpdateSelf) {
   //    dispatch(usersGetSelfStart({}));
   onClose();
  }
 }, [success?.usersUpdateSelf, dispatch, onClose]);

 useEffect(() => {
  if (success?.identitySetTotp && identityState.qrCode) {
   setQrCode(identityState.qrCode?.uri);
  }
 }, [success?.identitySetTotp, identityState.qrCode]);

 useEffect(() => {
  if (success?.identityVerifyTotp) {
   dispatch(usersGetSelfStart({}));
   setQrCode(null);
   setVerificationCode('');
  }
 }, [success?.identityVerifyTotp, dispatch]);

 const handleFormSubmit = (data: UserProfileFormData) => {
  if (activeTab === 0) {
   dispatch(
    usersUpdateSelfStart({
     Name: data.Name || '',
     Surname: data.Surname || '',
     Email: data.Email || self?.Email || '',
     Phone: data.Phone || self?.Phone || '',
     TwoFAEnabled: self?.TwoFAEnabled || false,
     UserGroups: data.UserGroups || [],
     SendMethods: data.SendMethods || [],
     Customer: data.Customer || null,
     ProfileImageBase64: self?.ProfileImageBase64 || null,
    }),
   );
  } else if (activeTab === 1) {
   dispatch(
    usersUpdateSelfStart({
     //  OldPassword: data.OldPassword,
     //  NewPassword: data.NewPassword,
     Email: data.Email,
     Phone: data.Phone,
     Name: self?.Name || '',
     Surname: self?.Surname || '',
     TwoFAEnabled: self?.TwoFAEnabled || false,
     UserGroups: self?.UserGroups || [],
     Customer: self?.Customer || null,
     ProfileImageBase64: self?.ProfileImageBase64 || null,
    }),
   );
  }
 };

 const handleSaveClick = () => {
  if (activeTab === 0) {
   handleSubmit(handleFormSubmit)();
  } else {
   const form = document.getElementById(
    'password-change-form',
   ) as HTMLFormElement;
   if (form) {
    form.requestSubmit();
   }
  }
 };

 const handleCancel = () => {
  reset(defaultValues);
  setTwoFactorEnabled(false);
  onClose();
 };

 const handleGenerateQR = () => {
  const userId = getCookieCustom('user_id');
  if (userId) {
   dispatch(
    identitySetTotpStart({
     userid: userId,
     sessionId: undefined,
    }),
   );
  }
 };

 const handle2FACodeSubmit = (fieldName: string, value: string) => {
  const userId = getCookieCustom('user_id');
  if (userId && value) {
   dispatch(
    identitySetVerificationStart({
     Method: fieldName === 'email' ? 1 : 2,
     UserId: userId,
    }),
   );
  }
 };

 const handleVerifyTotp = () => {
  const userId = getCookieCustom('user_id');
  if (userId && verificationCode) {
   dispatch(
    identityVerifyTotpStart({
     userid: userId,
     sessionId: undefined,
     code: verificationCode,
    }),
   );
   setVerificationCode('');
   setQrCode(null);
  }
 };

 return {
  control,
  errors,
  watch,
  setValue,
  handleSubmit,
  handleFormSubmit,
  handleSaveClick,
  handleCancel,
  activeTab,
  setActiveTab,
  self,
  groups,
  loading,
  twoFactorAuthMethod,
  setTwoFactorAuthMethod,
  twoFactorEnabled,
  setTwoFactorEnabled,
  qrCode,
  setQrCode,
  verificationCode,
  setVerificationCode,
  handleGenerateQR,
  handle2FACodeSubmit,
  handleVerifyTotp,
  i18n,
 };
};

